<script lang="ts">
	// Svelte
	import { getContext, tick } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';
	//Helpers
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import {
		scrollToAdditionalContent,
		scrollToElementInWizard,
	} from 'src/components/Lists/list-helpers';
	import { mappedDeviceOperator } from 'src/components/Scenes/components/GenericView/extract_conditions_variables';
	//Design System
	import { Text, Select, Token, Input, Radio, IconButton, Alert } from '@Shelly/shelly-design-system';
	import { capFirstChar, conditionsFunc, removeOrAddUnderscore } from 'src/helpers/utilities';
	import { getDevicePropTranslation } from './helpers/translation-helpers';
	import { WHEN_WIZARD_STEPS_INDEXES } from '../../helpers/when-wizard-helpers';
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { roller_is_calibrated } from 'src/helpers/requests/calibrate_cover';
	import {
		isBluRC,
		isCameraDevice,
		isDistance,
		isRgbwwPmRelatedChannel,
	} from 'src/helpers/interface_helpers';
	import KeyValueCondition from './DeviceProps/KeyValueCondition.svelte';
	import TextCondition from './DeviceProps/TextCondition.svelte';
	import PresenceDeviceCondition from './DeviceProps/PresenceDeviceCondition.svelte';
	import CameraDeviceCondition from './DeviceProps/CameraDeviceCondition.svelte';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import { isVCType } from 'src/components/Scenes/helpers/virtual_components/virtual_component_utils';
	import { real_id } from 'src/helpers/interface_helpers';
	// Presence-specific helpers
	import {
		isPresenceDevice,
		getPresenceZones,
	} from 'src/components/Scenes/helpers/device_conditions/custom_cases/presence/presence-device-helpers';
	import { getCameraMotionZones } from 'src/components/Scenes/helpers/device_conditions/custom_cases/camera/camera-device-helpers';
	import { ventoraClamp } from 'src/components/DevicesSettings/Settings/SettingsG2/VentoraFan/ventora_fan_settings_helpers';

	export let sceneData: any = {};
	export let device = { title: '', value: '' };
	export let disabled = false;
	export let values = [],
		icon = null,
		input_value,
		input_text,
		config,
		subtitle = '',
		id = '',
		sensor_name,
		alert = false,
		i18tr = '',
		character = '',
		add_info_ind,
		input_index,
		list = [],
		key_values = [];

	let valueOutOfRange: boolean = false;
	let invalidInputError: boolean = false;
	let coverPositionError = false;

	// Optimized device options mapping
	const devicesOptions = list.map((d) => ({
		title: d.name,
		value: d.id,
	}));

	let additionalElementRef;
	let addConditionContainersRefs: SvelteStore<{
		wizardContainerRef: HTMLElement;
		scrollContainerRef: HTMLElement;
	}> = getContext('addConditionContainersRefs');

	let additionalElHeight;
	let oldAdditionalElHeight;

	$: deviceType = $devices[sceneData?.selectedDevice?.id]?.type || '';

	$: statusStore = getDeviceStatus(sceneData?.selectedDevice?.id);
	$: settingsStore = getDeviceSettings(sceneData?.selectedDevice?.id);

	/**
	 * Pro RGBWW PM "related" (non-main) channels intentionally hide output state,
	 * brightness and power consumption conditions — these belong to the main
	 * channel. Surface an info alert so users understand why those tokens are absent.
	 * @see https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2999#note_473023
	 */
	$: rgbwwPmSelectedDevice =
		$devices[sceneData?.selectedDevice?.value] || $devices[sceneData?.selectedDevice?.id];
	$: rgbwwPmSelectedChannel = String(
		sceneData?.selectedDevice?.value ?? sceneData?.selectedDevice?.id ?? '',
	).split('_')[1];
	$: showRgbwwPmRelatedChannelAlert = isRgbwwPmRelatedChannel(
		rgbwwPmSelectedDevice?.type,
		rgbwwPmSelectedDevice?.rgbww_profile,
		rgbwwPmSelectedChannel,
	);

	$: isRollerCalibrated = roller_is_calibrated(
		$devices[sceneData?.selectedDevice?.id],
		$statusStore,
		$settingsStore,
	);

	$: showRemoteControlChannelSelect =
		isBluRC($devices[sceneData?.selectedDevice?.id]?.type) &&
		(sceneData.selectedDeviceProp?.value?.includes('push_type') ||
			sceneData.selectedDeviceProp?.value?.includes('scroll_position'));

	// Check if selected device is S4SN-0U61X (Presence sensor)
	$: currentIsPresenceDevice = isPresenceDevice(sceneData?.selectedDevice?.id, $devices);

	$: currentIsCameraDevice = isCameraDevice($devices[sceneData?.selectedDevice?.id]);

	// Extract zones from presence device settings
	$: selectedDeviceSettingsMap = (() => {
		const id = real_id(sceneData?.selectedDevice?.id);
		if (!id) return {};
		const settings = readDeviceSettings(id);
		return settings ? { [id]: settings } : {};
	})();

	$: currentPresenceZones = getPresenceZones(sceneData?.selectedDevice?.id, selectedDeviceSettingsMap);

	$: currentCameraMotionZones = getCameraMotionZones(
		sceneData?.selectedDevice?.id,
		selectedDeviceSettingsMap,
	);

	$: usesCustomDeviceConditionUi = currentIsPresenceDevice || currentIsCameraDevice;

	// Dynamically modify device properties for presence devices
	$: effectiveDevicePropsOptions = (() => {
		if (!currentIsPresenceDevice) {
			return sceneData.devicePropsOptions || [];
		}

		// For presence devices, delegate to the presence component
		return sceneData.devicePropsOptions || [];
	})();

	$: active = {
		operator: sceneData._device_condition?.operator || undefined,
		value: sceneData._device_condition?.value || undefined,
	};

	$: input_val = typeof input_value !== 'undefined' ? active.value || input_value : input_value;

	// Helper function to create select list item
	const createSelectListItem = (el: string, i: number) => {
		const currentOperator = mappedDeviceOperator(active.operator);

		// Handle remote control channel selection
		if (/channel_/.test(el) && el.includes(active.value)) {
			active.value = el;
		}

		return {
			selected:
				el === active.value ||
				el === removeOrAddUnderscore(currentOperator, 'removeUnderscore') ||
				el === currentOperator,
			id: i,
			title: el,
			value: el.toLowerCase().replace(' ', '_'),
		};
	};

	// Optimized select list generation
	$: selectList = (values || []).map(createSelectListItem);

	$: disabled = invalidInputError || valueOutOfRange;

	$: min = constraint_by_id(id, deviceType).min;
	$: max = constraint_by_id(id, deviceType).max;
	$: hasContent = values?.length > 0 || typeof alert === 'string';

	// Optimized selected device calculation
	$: selectedDevice = (() => {
		if (list.length > 0 && active.value) {
			return (
				devicesOptions.find((el) => el.value === active.value) || {
					title: '',
					value: undefined,
				}
			);
		}
		return {
			title: '',
			value: undefined,
		};
	})();

	// Helper function to format channel title
	const formatChannelTitle = (channelValue: string | undefined) => {
		if (!channelValue || channelValue === 'any') {
			return $format('label_scene_from_any', { default: 'From any' });
		}
		const part = String(channelValue).split('_')[1];
		const channelNumber = part !== undefined ? +part + 1 : 1;
		return $format('label_channel', {
			default: 'Channel',
			values: { channel: channelNumber },
		});
	};

	// Generate remote control channel options
	const remoteControlChannelOptions = [
		{
			title: $format('label_scene_from_any', { default: 'From any' }),
			value: 'any',
		},
		...Array.from({ length: 4 }, (_, i) => ({
			title: $format('label_channel', {
				default: 'Channel',
				values: { channel: i + 1 },
			}),
			value: `channel_${i}`,
		})),
	];

	$: selectedChannelRemote = (() => {
		const channelValue = sceneData?._device_condition?.remote_control_channel || 'any';
		return {
			title: formatChannelTitle(channelValue),
			value: channelValue,
		};
	})();

	// Validate input value
	const validateInputValue = () => {
		invalidInputError = `${input_val}`.length === 0;
		valueOutOfRange = Number(input_val) < min || Number(input_val) > max;
		coverPositionError =
			['roller position', 'multi_cover_position'].includes(sceneData.selectedDeviceProp?.value) &&
			typeof input_val !== 'undefined' &&
			conditionsFunc(input_val);
	};

	function handleInputError(value) {
		if (invalidInputError) {
			return $format('scene_invalid_format_err_toast', {
				default: 'Invalid format. Please, fill out fields properly.',
			});
		} else if (valueOutOfRange) {
			return $format('error_out_of_range', {
				default: `Value ${value} ${character} is out of range. Please provide a value within the limits. (${min} ${character} — ${max} ${character})`,
				values: { value, max, min, unit: character },
			});
		} else if (coverPositionError) {
			return $format('value_must_be_equal_or_greater_than_zero', {
				default: 'Value must be an integer and equal or greater than 0.',
			});
		}
	}

	// Constraint configuration map for better performance
	const CONSTRAINT_MAP: Record<string, string[] | { min: number; max: number }> = {
		// 0-100 range
		'0-100': [
			'battery',
			'humidity',
			'moisture',
			'brightness',
			'white_channel',
			'roller position',
			'multi_cover_position',
			'valve_percent_position',
			'electricLevel',
			'percent',
			'fan_speed',
		],
		// 0+ range
		'0+': ['solar_input_power', 'output_home_power', 'outputLimit', 'scroll_steps'],
		// Specific ranges
		temperature: { min: -100, max: 300 },
		meter: { min: -22000, max: 22000 },
		direction: { min: 0, max: 359 },
		uv: { min: 0, max: 15 },
		speed: { min: 0, max: 40 },
		gust_speed: { min: 0, max: 40 },
		pressure: { min: 300, max: 1200 },
		presence_zone_objects: { min: 0, max: 5 },
		dist: { min: 200, max: 5000 },
	};

	const METER_LIMITS: Record<string, { min: number; max: number }> = {
		'SPEM-003CEBEU400': { min: -112000, max: 112000 }, // 280V * 400A
		'SPEM-003CEBEU': { min: -33000, max: 33000 }, // 280V * 120A
	};

	const constraint_by_id = (id: string, type?: string) => {
		if (id === 'meter' && type && type in METER_LIMITS) {
			return METER_LIMITS[type];
		}

		// Check if id exists as a direct key in CONSTRAINT_MAP
		if (id in CONSTRAINT_MAP) {
			const constraint = CONSTRAINT_MAP[id];
			if (typeof constraint === 'object' && 'min' in constraint && 'max' in constraint) {
				return constraint;
			}
		}

		// Check predefined ranges for grouped properties
		for (const [range, constraint] of Object.entries(CONSTRAINT_MAP)) {
			if (Array.isArray(constraint) && constraint.includes(id)) {
				if (range === '0-100') return { min: 0, max: 100 };
				if (range === '0+') return { min: 0, max: 1000000 };
			}
		}

		// Handle virtual component types
		if (isVCType(id)) {
			return {
				min: config?.min ?? -1000000,
				max: config?.max ?? 1000000,
			};
		}

		// Default range
		return { min: -1000000, max: 1000000 };
	};

	const handle_condition_threshold = (item) => {
		if (sceneData._device_condition.cond_name !== item.id) {
			sceneData._device_condition.cond_name = item.id;
		}
		sceneData._device_condition.operator = item.operator;
		sceneData._device_condition.value = item.value;

		// Set additional fields if provided
		if (item.add_info_ind !== undefined) {
			sceneData._device_condition.add_info_ind = item.add_info_ind;
		}
		if (item.input_index !== undefined) {
			sceneData._device_condition.input_index = item.input_index;
		}

		if (import.meta.env.DEV) {
			console.log(
				'handle_condition_threshold item:',
				item,
				'sceneData._device_condition:',
				sceneData._device_condition,
			);
		}
	};

	const handle_input = (value) => {
		const numericValue = Number(value);
		input_val =
			id === 'fan_speed' && `${value}`.length > 0 && Number.isFinite(numericValue)
				? ventoraClamp(numericValue, min, max)
				: value;
		validateInputValue();
		validateWizardDeviceCondition();

		handle_condition_threshold({
			id,
			operator: selectList.find((e) => e?.selected)?.value,
			value: input_val,
			sensor_name,
			add_info_ind,
			input_index,
		});
	};

	$: if (
		id === 'fan_speed' &&
		typeof input_val !== 'undefined' &&
		`${input_val}`.length > 0 &&
		Number.isFinite(Number(input_val)) &&
		(Number(input_val) < min || Number(input_val) > max)
	) {
		handle_input(Number(input_val));
	}

	const change_active = (array, i: number, multipleSelect = false) => {
		array = array.map((sc) => {
			if (sc.id === i) {
				return {
					...sc,
					selected: multipleSelect ? !sc.selected : true,
				};
			}
			return {
				...sc,
				selected: multipleSelect ? sc.selected : false,
			};
		});

		return array;
	};

	const handle_condition = (_val) => {
		selectList = change_active(selectList, _val.id);

		validateInputValue();
		validateWizardDeviceCondition();

		handle_condition_threshold({
			id,
			operator: typeof input_value === 'undefined' ? 'equal' : _val.value,
			value: typeof input_value === 'undefined' ? _val.title : input_val,
			sensor_name,
			add_info_ind,
			input_index,
		});
	};

	const handle_condition_from_select_list = (e: CustomEvent) => {
		selectedDevice = { ...e.detail };

		handle_condition_threshold({
			id,
			operator: 'equal',
			value: parseInt(selectedDevice.value, 16).toString(),
			sensor_name,
			add_info_ind,
			input_index,
		});
	};

	// Helper functions for event handling
	const handleInputChange = (e: Event) => {
		const target = e.target as HTMLInputElement;
		const value = target?.value || '';

		// Update the condition value
		sceneData._device_condition.value = value;

		// For standard devices, use the existing flow
		handle_input(value);
	};

	const handleChannelChange = (e: CustomEvent) => {
		const detail = e.detail as any;
		sceneData._device_condition.remote_control_channel = detail?.value;
	};

	const handle_cond_on_change = (deviceProp) => {
		//1. update selected condition with new one
		// selectedCondition = { ...e.detail };
		sceneData.selectedDeviceProp = deviceProp;
		//2. reset operator and threshold value

		sceneData._device_condition.operator = '';
		sceneData._device_condition.value = '0';

		// Validate input value after reset, to cleat the error if there is one
		validateInputValue();

		//3. find the selected property and update temporary object  (cond__name and add_indo_ind)

		let foundCond = sceneData.components.find((el) => {
			return typeof el.props.add_info_ind !== 'undefined'
				? `${el.props.id}${el.props.add_info_ind}` === sceneData.selectedDeviceProp.value
				: typeof el.props.input_index !== 'undefined'
					? `${el.props.id}${el.props.input_index}` === sceneData.selectedDeviceProp.value
					: el.props.id === sceneData.selectedDeviceProp.value;
		});
		if (foundCond) {
			let concatUniqueID = foundCond
				? typeof foundCond?.props.add_info_ind !== 'undefined'
					? `${foundCond.props.id}${foundCond.props.add_info_ind}`
					: typeof foundCond?.props?.input_index !== 'undefined'
						? `${foundCond.props.id}${foundCond.props.input_index}`
						: foundCond.props.id
				: null;
			if (concatUniqueID === sceneData.selectedDeviceProp.value) {
				//update temp element with new selected condition brigtness/turn/open/temperature
				sceneData._device_condition.cond_name = foundCond.props.id;
				sceneData._device_condition.add_info_ind = foundCond.props.add_info_ind;
				sceneData._device_condition.input_index = foundCond.props.input_index;
				if (foundCond.props.id === 'scroll_steps') {
					delete sceneData._device_condition.remote_control_channel;
				}
			} else {
				sceneData._device_condition.cond_name = '';
				sceneData._device_condition.add_info_ind = undefined;
				sceneData._device_condition.input_index = undefined;
			}
		}
	};

	const onSelectDeviceProp = async (deviceProp) => {
		validateWizardDeviceProp();

		// Standard device property handling
		handle_cond_on_change(deviceProp);

		await tick();

		let heightIsDifferent = false;

		if (oldAdditionalElHeight !== additionalElHeight) {
			oldAdditionalElHeight = additionalElHeight;
			heightIsDifferent = true;
		}

		if (heightIsDifferent) {
			scrollToElementInWizard(
				$addConditionContainersRefs?.wizardContainerRef,
				additionalElementRef,
				$addConditionContainersRefs?.scrollContainerRef,
			);
		} else {
			scrollToAdditionalContent(
				additionalElementRef,
				$addConditionContainersRefs.scrollContainerRef,
			);
		}
	};

	const validateWizardDeviceProp = () => {
		sceneData.isValid = false;

		if (sceneData.when_info.mode === 'edit') {
			sceneData.validStepIndex = WHEN_WIZARD_STEPS_INDEXES.DEVICE_PROPS_STEP;
		}
	};

	const validateWizardDeviceCondition = () => {
		const isSelectList = selectList?.length > 0;
		const isActiveCondition = selectList?.findIndex((e) => e?.selected) !== -1;

		// Standard validation for all devices
		sceneData.isValid =
			(!isSelectList || isActiveCondition) &&
			!invalidInputError &&
			!valueOutOfRange &&
			!coverPositionError;
		sceneData.validStepIndex = WHEN_WIZARD_STEPS_INDEXES.DEVICE_PROPS_STEP;
	};

	$: {
		const firstAvailableDeviceProp = sceneData.devicePropsOptions?.find(
			(option) => !option.disabled,
		);

		// S4SN presence uses PresenceDeviceCondition — skip generic “first property” auto-pick
		if (
			firstAvailableDeviceProp &&
			!usesCustomDeviceConditionUi &&
			sceneData.when_info.mode === 'edit' &&
			((!isDistance($devices?.[sceneData?.selectedDevice?.id]?.type) &&
				!sceneData?.selectedDeviceProp?.value) ||
				(['roller position', 'multi_cover_position'].includes(
					sceneData?.selectedDeviceProp?.value,
				) &&
					!isRollerCalibrated))
		) {
			onSelectDeviceProp(firstAvailableDeviceProp);
		}

		if (
			!usesCustomDeviceConditionUi &&
			sceneData.when_info.mode === 'edit' &&
			!sceneData?.selectedDeviceProp?.value &&
			isDistance($devices?.[sceneData?.selectedDevice?.id]?.type)
		) {
			validateWizardDeviceProp();
		}
	}

	// Event handlers for presence device integration
	function handlePresenceConditionChange(event) {
		const condition = event.detail.condition;
		handle_condition_threshold(condition);
	}

	function handlePresenceValidationChange(event) {
		const isValid = event.detail.isValid;
		if (currentIsPresenceDevice) {
			sceneData.isValid = isValid;
			sceneData.validStepIndex = WHEN_WIZARD_STEPS_INDEXES.DEVICE_PROPS_STEP;
		}
	}

	function handlePresenceDevicePropChange(event) {
		const deviceProp = event.detail.deviceProp;
		sceneData.selectedDeviceProp = deviceProp;

		// Reset condition data
		sceneData._device_condition.operator = '';
		sceneData._device_condition.value = '0';
		sceneData._device_condition.cond_name = '';
		sceneData._device_condition.add_info_ind = undefined;
		sceneData._device_condition.input_index = undefined;

		validateInputValue();
	}

	function handleCameraConditionChange(event) {
		const condition = event.detail.condition;
		handle_condition_threshold(condition);
	}

	function handleCameraValidationChange(event) {
		const isValid = event.detail.isValid;
		if (currentIsCameraDevice) {
			sceneData.isValid = isValid;
			sceneData.validStepIndex = WHEN_WIZARD_STEPS_INDEXES.DEVICE_PROPS_STEP;
		}
	}

	function handleCameraDevicePropChange(event) {
		const deviceProp = event.detail.deviceProp;
		sceneData.selectedDeviceProp = deviceProp;

		sceneData._device_condition.operator = '';
		sceneData._device_condition.value = '0';
		sceneData._device_condition.cond_name = '';
		sceneData._device_condition.add_info_ind = undefined;
		sceneData._device_condition.input_index = undefined;

		validateInputValue();
	}
</script>

<Text element="h2" class="mb-s">
	{$format('label_select_device_property', {
		default: 'Select device property',
	})}
</Text>

{#if currentIsCameraDevice}
	<CameraDeviceCondition
		{sceneData}
		motionZones={currentCameraMotionZones}
		{disabled}
		on:conditionChange={handleCameraConditionChange}
		on:validationChange={handleCameraValidationChange}
		on:devicePropChange={handleCameraDevicePropChange}
	/>
{:else if currentIsPresenceDevice}
	<!-- Presence Device Component -->
	<PresenceDeviceCondition
		{sceneData}
		presenceZones={currentPresenceZones}
		{disabled}
		on:conditionChange={handlePresenceConditionChange}
		on:validationChange={handlePresenceValidationChange}
		on:devicePropChange={handlePresenceDevicePropChange}
	/>
{:else}
	<!-- Standard Device Properties -->
	{#if showRgbwwPmRelatedChannelAlert}
		<Alert
			class="mb-s"
			type="info"
			heading={$format('scene_condition_rgbwwpm_related_channel_alert', {
				default:
					'Output state, brightness and power consumption can only be used as conditions on the main channel of this device.',
			})}
		/>
	{/if}
	<div
		class={classMap({
			'd-flex flex-wrap': true,
			'mb-l': sceneData.selectedDeviceProp && hasContent,
		})}
		style="gap:var(--spacing-s)"
	>
		{#each effectiveDevicePropsOptions as deviceProp}
			<Token
				id="token-araiwk2"
				title={capFirstChar(deviceProp.title)}
				class="text-capitalize"
				on:click={() => onSelectDeviceProp(deviceProp)}
				selected={sceneData.selectedDeviceProp.value === deviceProp.value}
				disabled={deviceProp.disabled ||
					(sceneData.when_info.mode === 'edit' &&
						((!isDistance($devices?.[sceneData?.selectedDevice?.id]?.type) &&
							!sceneData?.selectedDeviceProp?.value) ||
							(['roller position', 'multi_cover_position'].includes(
								sceneData.selectedDeviceProp.value,
							) &&
								!isRollerCalibrated)))}
			/>
		{/each}
	</div>
{/if}

{#if sceneData.selectedDeviceProp}
	<!-- {#if sensor_name}
			<Text class="mb-s" styleType="h2">{sensor_name}</Text>
		{/if} -->

	{#key sceneData.selectedDeviceProp.value}
		<div bind:this={additionalElementRef} bind:clientHeight={additionalElHeight}>
			{#if typeof alert === 'string'}
				<!-- TODO ask Alex when should we get this variable as string and should we render Alert instead of text -->
				<Text class="mb-s" styleType="h2">{alert}</Text>
			{/if}
			{#if values?.length > 0 && !usesCustomDeviceConditionUi}
				<!-- TODO desing unify the titles and think how to represent when there is sensor_name -->
				<Text class="mb-s" styleType="h2">
					{getDevicePropTranslation(i18tr, sensor_name, input_val)?.title}
				</Text>
				<div class="d-flex flex-wrap" style="gap:var(--spacing-s)">
					{#each selectList as _val, i}
						{@const currentTranslation = getDevicePropTranslation(
							i18tr,
							sensor_name,
							input_val,
							_val.title,
							character,
						)}
						<Token
							id="token-rxhnmk6"
							size="m"
							title={currentTranslation?.condition}
							selected={_val.selected}
							icon={currentTranslation?.icons[_val.title]}
							on:click={() => handle_condition(_val)}
						/>
					{/each}
				</div>
			{/if}
			{#if key_values?.length > 0}
				<KeyValueCondition
					{sceneData}
					{id}
					{subtitle}
					{sensor_name}
					{add_info_ind}
					{input_index}
					{key_values}
					bind:active
					{handle_condition_threshold}
				/>
			{/if}
			{#if typeof input_val !== 'undefined' && !usesCustomDeviceConditionUi}
				<div class="d-flex mt-s">
					<IconButton
						aria-label={$format('button_decrease_value', {
							default: 'Decrease value',
						})}
						icon="fas fa-minus"
						on:click={() => {
							handle_input(+input_val - 1);
						}}
						disabled={+input_val <= min}
					/>
					<Input
						aria-label={$format('input_set_value', { default: 'Set value' })}
						{...getNumberInputConfig(true)}
						class="width-md-s width-sm-full ml-s mr-s mb-l"
						size="m"
						{icon}
						value={sceneData._device_condition?.value}
						error={handleInputError(sceneData._device_condition?.value)}
						on:input={handleInputChange}
						id="input-13nhets"
					/>
					<IconButton
						aria-label={$format('button_increase_value', {
							default: 'Increase value',
						})}
						icon="fas fa-plus"
						on:click={() => {
							handle_input(+input_val + 1);
						}}
						disabled={+input_val >= max}
					/>
				</div>
			{/if}
			{#if typeof input_text !== 'undefined'}
				<TextCondition
					{sceneData}
					{id}
					{icon}
					{sensor_name}
					{add_info_ind}
					{input_index}
					{input_text}
					{config}
					bind:active
					bind:invalidInputError
					bind:valueOutOfRange
					{handle_condition_threshold}
					{validateWizardDeviceCondition}
				/>
			{/if}
			{#if list.length > 0}
				<!-- TODO as Alex is there device to test this condition -->
				<Text class="mb-s" styleType="h2"
					>{$format('select_observer', {
						default: 'Select observer device',
					})}</Text
				>
				<Select
					aria-labelledby="select-oqznqo2"
					icon="fas fa-microchip"
					size="m"
					class="mb-s"
					options={devicesOptions}
					value={selectedDevice}
					label={$format('label_select_device', {
						default: 'Select device',
					})}
					disabled={list.length === 0}
					on:change={(e) => handle_condition_from_select_list(e)}
					id="select-qa56f7x"
				/>
				<Radio selected={device.title} value={device.title} size="m" id="radio-30u4azf">
					{$format('scene_condition_observer_reports_device', {
						values: { observer: selectedDevice.title, device: device.title },
						default: `When ${selectedDevice.title} reports ${device.title} button status`,
					})}
				</Radio>
			{/if}

			{#if showRemoteControlChannelSelect}
				<Text class="mb-s" styleType="h2">
					{$format('label_channels', { default: 'Channel' })}
				</Text>
				<Select
					icon="fas fa-hashtag"
					size="m"
					class="mb-s"
					options={remoteControlChannelOptions}
					value={selectedChannelRemote}
					label={$format('prop_channel', { default: 'Channel' })}
					on:change={handleChannelChange}
					id="select-qh6k3el"
					aria-labelledby="select-qh6k3el"
				/>
			{/if}
		</div>
	{/key}
{/if}
